import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import type { Profile } from '@/types/database'

export default function AdminUsersPage() {
  const [users, setUsers] = useState<Profile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const limit = 20

  useEffect(() => {
    fetchUsers()
  }, [page, statusFilter])

  const fetchUsers = async () => {
    setIsLoading(true)

    try {
      let query = supabase.from('profiles').select('*', { count: 'exact' })

      if (statusFilter) {
        query = query.eq('subscription_status', statusFilter)
      }

      if (search) {
        query = query.ilike('email', `%${search}%`)
      }

      const { count } = await query

      setTotalPages(Math.ceil((count || 0) / limit))

      let dataQuery = supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })
        .range((page - 1) * limit, page * limit - 1)

      if (statusFilter) {
        dataQuery = dataQuery.eq('subscription_status', statusFilter)
      }

      if (search) {
        dataQuery = dataQuery.ilike('email', `%${search}%`)
      }

      const { data } = await dataQuery as { data: Profile[] | null }

      setUsers(data || [])
    } catch (error) {
      console.error('Failed to fetch users:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    fetchUsers()
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge-success">Active</span>
      case 'trialing':
        return <span className="badge-warning">Trial</span>
      case 'canceled':
        return <span className="badge-gray">Canceled</span>
      case 'past_due':
        return <span className="badge-danger">Past Due</span>
      default:
        return <span className="badge-gray">{status}</span>
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">User Management</h1>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 flex flex-wrap gap-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email..."
            className="input bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          />
          <button type="submit" className="btn-primary">Search</button>
        </form>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          className="input bg-gray-700 border-gray-600 text-white"
        >
          <option value="">All statuses</option>
          <option value="trialing">Trialing</option>
          <option value="active">Active</option>
          <option value="canceled">Canceled</option>
          <option value="past_due">Past Due</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
        </div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Trial Ends</th>
                  <th className="px-4 py-3 font-medium">Events</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-white">
                      {user.email}
                      {user.is_disabled && (
                        <span className="ml-2 text-xs text-red-400">(Disabled)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.role === 'admin' ? (
                        <span className="badge bg-red-500/20 text-red-300">Admin</span>
                      ) : (
                        <span className="badge bg-gray-600 text-gray-300">User</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(user.subscription_status)}
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {user.trial_ends_at
                        ? format(new Date(user.trial_ends_at), 'MMM d, yyyy')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {user.events_count.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {format(new Date(user.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/users/${user.id}`}
                        className="text-primary-400 hover:text-primary-300 font-medium"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm bg-gray-700 text-gray-300 rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm bg-gray-700 text-gray-300 rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
